import { Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { favoriteCustomEmoji, favoriteCustomEmojiSet, useEmojiCatalog } from "../../emoji";
import { commonStandardEmoji, standardEmojiMatches } from "../../standard-emoji";

function favoriteMatches(catalog: Record<string, string>, query: string) {
  return favoriteCustomEmoji.flatMap((name) => {
    const url = catalog[name];
    return name.includes(query) && url && !url.startsWith("alias:") ? ([[name, url]] as const) : [];
  });
}

function EmojiButton({
  name,
  url,
  unicode,
  onPick,
}: {
  name: string;
  url?: string;
  unicode?: string;
  onPick: (name: string, url?: string) => void;
}) {
  return (
    <button
      type="button"
      title={`:${name}:`}
      aria-label={`:${name}:`}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => onPick(name, url)}
    >
      {url ? <img src={url} alt="" loading="lazy" /> : <span>{unicode}</span>}
    </button>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="emoji-section">
      <div className="emoji-section-label">{label}</div>
      <div className="emoji-grid">{children}</div>
    </div>
  );
}

export function EmojiPicker({
  onPick,
  onClose,
}: {
  onPick: (name: string, url?: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const { catalog: custom, loading, error } = useEmojiCatalog();
  const searchRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    searchRef.current?.focus();
  }, []);
  const normalizedQuery = query.trim().toLowerCase();
  const standard = normalizedQuery ? standardEmojiMatches(normalizedQuery, 120) : standardEmojiMatches("", 120);

  const customAll = Object.entries(custom)
    .filter(([name, url]) => !favoriteCustomEmojiSet.has(name) && !url.startsWith("alias:"))
    .sort(([leftName], [rightName]) => leftName.localeCompare(rightName));

  let body: React.ReactNode;
  if (loading) {
    body = (
      <Section label="Emoji">
        {standard.map((emoji) => (
          <EmojiButton key={emoji.name} {...emoji} onPick={onPick} />
        ))}
      </Section>
    );
  } else if (error && !Object.keys(custom).length) {
    body = (
      <>
        <Section label="Emoji">
          {standard.map((emoji) => (
            <EmojiButton key={emoji.name} {...emoji} onPick={onPick} />
          ))}
        </Section>
        <div className="picker-status picker-status--error">Workspace emoji couldn’t load.</div>
      </>
    );
  } else if (!normalizedQuery) {
    const favorites = favoriteMatches(custom, "");
    const rest = customAll.slice(0, 120);
    body = (
      <>
        <Section label="Frequently used">
          {commonStandardEmoji().map((emoji) => (
            <EmojiButton key={emoji.name} {...emoji} onPick={onPick} />
          ))}
        </Section>
        {favorites.length > 0 && (
          <Section label="Workspace favorites">
            {favorites.map(([name, url]) => (
              <EmojiButton key={name} name={name} url={url} onPick={onPick} />
            ))}
          </Section>
        )}
        <Section label="Emoji">
          {standard
            .filter((emoji) => !commonStandardEmoji().some((common) => common.name === emoji.name))
            .map((emoji) => (
              <EmojiButton key={emoji.name} {...emoji} onPick={onPick} />
            ))}
        </Section>
        {rest.length > 0 && (
          <Section label="Workspace emoji">
            {rest.map(([name, url]) => (
              <EmojiButton key={name} name={name} url={url} onPick={onPick} />
            ))}
          </Section>
        )}
      </>
    );
  } else {
    const favorites = favoriteMatches(custom, normalizedQuery);
    const rest = customAll.filter(([name]) => name.includes(normalizedQuery)).slice(0, 120);
    body = (
      <>
        {standard.length > 0 && (
          <Section label="Emoji">
            {standard.map((emoji) => (
              <EmojiButton key={emoji.name} {...emoji} onPick={onPick} />
            ))}
          </Section>
        )}
        {favorites.length > 0 && (
          <Section label="Workspace favorites">
            {favorites.map(([name, url]) => (
              <EmojiButton key={name} name={name} url={url} onPick={onPick} />
            ))}
          </Section>
        )}
        {rest.length > 0 && (
          <Section label="Workspace emoji">
            {rest.map(([name, url]) => (
              <EmojiButton key={name} name={name} url={url} onPick={onPick} />
            ))}
          </Section>
        )}
      </>
    );
  }

  return (
    <div className="popover emoji-popover" role="dialog" aria-label="Emoji picker">
      <div className="popover-head">
        <strong>Emoji</strong>
        <button type="button" onClick={onClose} aria-label="Close emoji picker">
          <X size={16} />
        </button>
      </div>
      <label className="picker-search">
        <Search size={14} />
        <input
          ref={searchRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search emoji"
          onKeyDown={(event) => {
            if (event.key === "Escape") onClose();
            if (event.key === "ArrowDown") {
              event.preventDefault();
              resultsRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
            }
            if (event.key === "Enter" && normalizedQuery) {
              const firstStandard = standard[0];
              const firstCustom =
                favoriteMatches(custom, normalizedQuery)[0] ??
                customAll.find(([name]) => name.includes(normalizedQuery));
              if (firstStandard) {
                event.preventDefault();
                onPick(firstStandard.name);
              } else if (firstCustom) {
                event.preventDefault();
                onPick(firstCustom[0], firstCustom[1]);
              }
            }
          }}
        />
        {query && (
          <button type="button" aria-label="Clear search" onClick={() => setQuery("")}>
            <X size={13} />
          </button>
        )}
      </label>
      <div className="emoji-sections" ref={resultsRef}>
        {body}
      </div>
      {!loading &&
        normalizedQuery &&
        !standard.length &&
        !favoriteMatches(custom, normalizedQuery).length &&
        !customAll.some(([name]) => name.includes(normalizedQuery)) && (
          <div className="empty-picker">No emoji found for “{query}”</div>
        )}
    </div>
  );
}
